/**
 * Atlas Cognitive Navigation Integration
 * 
 * Integrates TD1.ATLAS into YourDreamAI for cognitive web navigation.
 * AIs use Atlas to navigate to information spatially, not search for it.
 * 
 * Key Principle: Atlas must never behave like a search engine.
 * Instead, it navigates a pre-built cognitive map using spatial reasoning.
 */

import type { Orientation } from "./types";

// Import Atlas types (we'll adapt them for TypeScript)
export interface AtlasLandmark {
  id: string;
  url: string;
  title: string;
  topicNeighbourhood: string;
  summary: string;
  structuredFacts: string[];
  reliabilityScore: number;
  content?: string;
  metadata?: Record<string, any>;
  location?: {
    coordinates: { lat: number; lng: number };
    address?: string;
    source: { title: string; url: string; type: 'web' | 'document' | 'search' };
  };
}

export interface AtlasTopic {
  id: string;
  name: string;
  description: string;
  nearbyTopics: Array<{ topicId: string; distance: number }>;
  commonUserIntents: string[];
  preferredLandmarks: string[];
  familiarity: number;
}

export interface AtlasRoute {
  id: string;
  from: string;
  to: string;
  type: 'often-used-with' | 'prerequisite' | 'deeper-explanation' | 'alternative-approach' | 'related';
  distance: number;
  description: string;
}

export interface AtlasNavigationResult {
  success: boolean;
  location?: {
    topicId: string;
    topicName: string;
    landmarks: AtlasLandmark[];
  };
  orientation?: {
    currentTopic: string;
    nearbyTopics: AtlasTopic[];
    message: string;
  };
  route?: {
    steps: Array<{ from: string; to: string; type: string }>;
    description: string;
  };
  suggestions?: string[];
  sources?: Array<{
    title: string;
    url: string;
    type: 'landmark' | 'topic' | 'document';
    relevance?: number;
  }>;
  locations?: Array<{
    name: string;
    coordinates: { lat: number; lng: number };
    address?: string;
    source: { title: string; url: string; type: 'web' | 'document' | 'search' };
    relevance?: number;
  }>;
  directions?: {
    origin: {
      name: string;
      coordinates: { lat: number; lng: number };
      address?: string;
    };
    destination: {
      name: string;
      coordinates: { lat: number; lng: number };
      address?: string;
    };
    distance: string;
    duration: string;
    steps: Array<{
      distance: string;
      duration: string;
      instruction: string;
      coordinates: { lat: number; lng: number };
    }>;
    mapUrl: string;
  };
  message: string;
}

export interface AtlasSearchProcess {
  status: 'planning' | 'navigating' | 'orienting' | 'complete' | 'ready' | 'skipped';
  queries?: string[];
  sources?: Array<{ name: string; url: string; type: 'landmark' | 'topic' }>;
  currentLocation?: string;
  nearbyAreas?: string[];
  message: string;
}

/**
 * Thought Stream Pipeline - How the assistant thinks (mirrors agent: read → plan → reason → act → respond).
 * Used to show transparent reasoning in Atlas and make it the most powerful cognitive map.
 */
export type ThoughtStreamPhase = 'read' | 'plan' | 'reason' | 'act' | 'respond';

export interface ThoughtStreamStep {
  phase: ThoughtStreamPhase;
  label: string;
  description?: string;
  completed: boolean;
  active?: boolean;
}

/** Map Atlas process status to Thought Stream Pipeline phase for UI. */
export function atlasStatusToThoughtPhase(status: AtlasSearchProcess['status']): ThoughtStreamPhase {
  switch (status) {
    case 'planning': return 'plan';
    case 'navigating': return 'reason';
    case 'orienting': return 'act';
    case 'complete':
    case 'ready': return 'respond';
    case 'skipped': return 'respond';
    default: return 'plan';
  }
}

/** Build full thought pipeline steps from current Atlas process (for "exactly what I have" display). */
export function buildThoughtStreamSteps(process: AtlasSearchProcess | null): ThoughtStreamStep[] {
  const phase = process ? atlasStatusToThoughtPhase(process.status) : 'read';
  const phases: ThoughtStreamPhase[] = ['read', 'plan', 'reason', 'act', 'respond'];
  const labels: Record<ThoughtStreamPhase, string> = {
    read: 'Read & parse',
    plan: 'Plan',
    reason: 'Reason',
    act: 'Act',
    respond: 'Respond',
  };
  const idx = phases.indexOf(phase);
  return phases.map((p, i) => ({
    phase: p,
    label: labels[p],
    completed: i < idx || (i === idx && (process?.status === 'complete' || process?.status === 'ready' || process?.status === 'skipped')),
    active: i === idx && process?.status !== 'complete' && process?.status !== 'ready' && process?.status !== 'skipped',
  }));
}

/**
 * Collapsible thought entry for in-depth thought stream (like Cursor's thought stream).
 * Each entry has a title, optional relative time, and rich body (analysis, steps, code, etc.).
 */
export type ThoughtEntryKind = 'analysis' | 'investigation' | 'reasoning' | 'action' | 'solution' | 'verification' | 'summary';

export interface ThoughtEntryBlock {
  type: 'paragraph' | 'bullets' | 'code' | 'steps' | 'quote';
  content: string | string[];
  language?: string;
}

export interface AtlasThoughtEntry {
  id: string;
  kind: ThoughtEntryKind;
  title: string;
  /** Relative time label for UI, e.g. "6s", "15s" */
  relativeTime?: string;
  /** In-depth body blocks (paragraphs, bullets, code, steps) */
  body: ThoughtEntryBlock[];
  /** Optional sub-actions (e.g. "Read SignInButton.tsx", "Grepped httpEndpointURL") */
  actions?: string[];
  completed?: boolean;
  active?: boolean;
}

/**
 * Build a comprehensive list of thought entries from Atlas process + result.
 * Gives the UI multiple collapsible "Thought Xs" blocks with in-depth coverage.
 */
export function buildThoughtStreamEntries(
  process: AtlasSearchProcess | null,
  result: AtlasNavigationResult | null
): AtlasThoughtEntry[] {
  const entries: AtlasThoughtEntry[] = [];
  if (!process) return entries;

  let t = 0;
  const ts = () => `${(t += 4) + 2}s`;

  // Thought 1: Planning – understanding where this information lives
  entries.push({
    id: 'planning',
    kind: 'reasoning',
    title: 'Planning cognitive navigation',
    relativeTime: ts(),
    completed: process.status !== 'planning',
    active: process.status === 'planning',
    body: [
      {
        type: 'paragraph',
        content: process.message || "Understanding where this information lives in the cognitive map.",
      },
      ...(process.queries && process.queries.length > 0
        ? [
            {
              type: 'bullets' as const,
              content: [
                'Navigation queries generated:',
                ...process.queries.map((q) => `"${q}"`),
              ],
            },
          ]
        : []),
    ],
    actions: process.queries?.length ? ['Building navigation queries', 'Mapping to cognitive areas'] : undefined,
  });

  // Thought 2: Navigating – moving through the map
  if (['navigating', 'orienting', 'complete', 'ready', 'skipped'].includes(process.status)) {
    entries.push({
      id: 'navigating',
      kind: 'action',
      title: 'Navigating spatially',
      relativeTime: ts(),
      completed: !['navigating'].includes(process.status),
      active: process.status === 'navigating',
      body: [
        {
          type: 'paragraph',
          content: `Moving through the cognitive map to find the right area.${process.currentLocation ? ` Current location: ${process.currentLocation}.` : ''}`,
        },
        ...(process.sources && process.sources.length > 0
          ? [
              {
                type: 'steps' as const,
                content: process.sources.map((s) => `${s.type === 'landmark' ? 'Landmark' : 'Topic'}: ${s.name}`),
              },
            ]
          : []),
      ],
      actions: process.sources?.length
        ? process.sources.map((s) => `Located: ${s.name}`)
        : ['Calling Atlas navigate API', 'Resolving orientation and anchors'],
    });
  }

  // Thought 3: Orienting – confirming location
  if (['orienting', 'complete', 'ready', 'skipped'].includes(process.status)) {
    const orientMsg =
      result?.orientation?.message ||
      `Orienting in ${process.currentLocation || 'the map'}. Confirming: "This information lives here."`;
    entries.push({
      id: 'orienting',
      kind: 'verification',
      title: 'Orienting in map',
      relativeTime: ts(),
      completed: !['orienting'].includes(process.status),
      active: process.status === 'orienting',
      body: [
        { type: 'paragraph', content: orientMsg },
        ...(result?.orientation?.nearbyTopics && result.orientation.nearbyTopics.length > 0
          ? [
              {
                type: 'bullets' as const,
                content: [
                  'Nearby topics in cognitive map:',
                  ...result.orientation.nearbyTopics.slice(0, 8).map((topic: { name?: string }) => topic.name || ''),
                ].filter(Boolean),
              },
            ]
          : []),
        ...(process.nearbyAreas && process.nearbyAreas.length > 0
          ? [{ type: 'bullets' as const, content: ['Nearby areas:', ...process.nearbyAreas] }]
          : []),
      ],
      actions: result?.orientation?.nearbyTopics?.length
        ? ['Locking orientation', 'Listing nearby topics']
        : ['Confirming location'],
    });
  }

  // Thought 4: Route – how we got here (if we have route data)
  if (result?.route && (process.status === 'complete' || process.status === 'ready' || process.status === 'skipped')) {
    entries.push({
      id: 'route',
      kind: 'summary',
      title: 'Navigation route',
      relativeTime: ts(),
      completed: true,
      body: [
        { type: 'paragraph', content: result.route.description || 'Path through the cognitive map.' },
        ...(result.route.steps && result.route.steps.length > 0
          ? [
              {
                type: 'steps' as const,
                content: result.route.steps.map((s: { from?: string; to?: string }) => `${s.from || '?'} → ${s.to || '?'}`),
              },
            ]
          : []),
      ],
      actions: ['Route computed', 'Steps recorded'],
    });
  }

  // Thought 5: Landmarks / discovery – what we found
  if (
    result?.location?.landmarks &&
    result.location.landmarks.length > 0 &&
    (process.status === 'complete' || process.status === 'ready' || process.status === 'skipped')
  ) {
    entries.push({
      id: 'landmarks',
      kind: 'solution',
      title: 'Landmarks discovered',
      relativeTime: ts(),
      completed: true,
      body: [
        {
          type: 'paragraph',
          content: `Found ${result.location.landmarks.length} landmark(s) in ${result.location.topicName || 'this area'}.`,
        },
        {
          type: 'bullets',
          content: result.location.landmarks.slice(0, 6).map((l: AtlasLandmark) => `${l.title}: ${l.summary?.slice(0, 80) || ''}${(l.summary?.length || 0) > 80 ? '…' : ''}`),
        },
      ],
      actions: result.location.landmarks.slice(0, 4).map((l: AtlasLandmark) => `Landmark: ${l.title}`),
    });
  }

  // Thought 6: Completion summary
  if (process.status === 'complete' || process.status === 'ready' || process.status === 'skipped') {
    const summaryParts: string[] = [];
    if (process.message?.trim()) summaryParts.push(process.message.trim());
    if (result?.orientation?.message?.trim()) summaryParts.push(result.orientation.message.trim());
    entries.push({
      id: 'complete',
      kind: 'summary',
      title: 'Navigation complete',
      relativeTime: ts(),
      completed: true,
      body: [
        {
          type: 'paragraph',
          content: summaryParts.length ? summaryParts.join(' ') : 'Found the right area in the cognitive map. Ready to provide information.',
        },
        {
          type: 'quote',
          content: 'Atlas is not search. It\'s spatial navigation through a pre-built cognitive map—knowing where information "lives."',
        },
      ],
      actions: ['Orientation locked', 'Ready to respond'],
    });
  }

  return entries;
}

/**
 * Atlas Integration for YourDreamAI
 * 
 * Provides cognitive navigation for AIs instead of traditional search.
 * Shows transparent navigation process with landmarks, topics, and routes.
 */
export class AtlasIntegration {
  private isInitialized: boolean = false;
  private navigationHistory: AtlasNavigationResult[] = [];
  private sessionId: string = `session-${Date.now()}`;
  private userId: string = "default-user";

  constructor() {
    // Atlas will use the real system
  }

  /**
   * Initialize Atlas (loads the Atlas system)
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Atlas is ready to use - no special initialization needed
      this.isInitialized = true;
      console.log("[Atlas] Initialized successfully");
    } catch (error) {
      console.error('Failed to initialize Atlas:', error);
    }
  }

  /**
   * Navigate to information using Atlas cognitive map
   * 
   * This is NOT search - it's spatial navigation.
   * Returns navigation result with landmarks and orientation.
   * 
   * Now uses server-side API endpoint to access process.env for API keys.
   */
  async navigate(
    query: string,
    context?: {
      currentTopic?: string;
      previousNavigations?: string[];
      userPreferences?: string[];
    }
  ): Promise<AtlasNavigationResult> {
    await this.initialize();

    console.log("[Atlas] Starting real navigation for:", query);

    try {
      // Prepare previous orientation for API
      const previousOrientation: Orientation | undefined = context?.currentTopic
        ? {
            location: context.currentTopic,
            area: "General",
            confidence: 0.7,
            context: {},
          }
        : undefined;

      // Call server-side API endpoint
      const response = await fetch("/api/atlas/navigate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query,
          mode: "cognitive",
          previousOrientation,
          sessionId: this.sessionId,
          userId: this.userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API request failed: ${response.status}`);
      }

      const navigation: AtlasNavigationResult = await response.json();

      console.log("[Atlas] Navigation complete:", {
        location: navigation.location?.topicName,
        landmarks: navigation.location?.landmarks?.length || 0,
        routes: navigation.route?.steps?.length || 0,
      });

      this.navigationHistory.push(navigation);
      return navigation;
    } catch (error: any) {
      console.error("[Atlas] Navigation failed:", error);
      
      // Fallback: human-friendly message so the UI never shows a raw "error" to the user
      return {
        success: false,
        location: {
          topicId: "general",
          topicName: "General Knowledge Area",
          landmarks: [],
        },
        orientation: {
          currentTopic: "General",
          nearbyTopics: [],
          message: "Using general knowledge. Ready to help with your question.",
        },
        message: "Ready to help.",
      };
    }
  }

  /**
   * Get transparent search process for UI display
   * 
   * Shows what Atlas is doing: planning, navigating, orienting
   * Now uses server-side API endpoint to access process.env for API keys.
   */
  async getSearchProcess(
    query: string,
    onUpdate?: (process: AtlasSearchProcess) => void
  ): Promise<AtlasSearchProcess> {
    await this.initialize();
    
    // Planning phase
    const planning: AtlasSearchProcess = {
      status: 'planning',
      message: `I'll navigate to the area where this information lives. Let me orient myself in the cognitive map.`,
      queries: this.generateNavigationQueries(query),
    };
    onUpdate?.(planning);
    await this.delay(500);

    // Navigating phase - call server-side API
    let navigationResult: AtlasNavigationResult | null = null;
    let orientation: Orientation = {
      location: "General Knowledge Area",
      area: "General",
      confidence: 0.5,
      context: {},
    };

    try {
      // Call the navigate API to get orientation, anchors, and routes
      const response = await fetch("/api/atlas/navigate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query,
          mode: "cognitive",
          sessionId: this.sessionId,
          userId: this.userId,
        }),
      });

      if (response.ok) {
        navigationResult = await response.json();
        orientation = {
          location: navigationResult.location?.topicName || "General Knowledge Area",
          area: navigationResult.orientation?.currentTopic || "General",
          confidence: 0.8,
          context: {},
        };
      }
    } catch (error) {
      console.error("[Atlas] Navigation API call failed:", error);
    }

    const navigating: AtlasSearchProcess = {
      ...planning,
      status: 'navigating',
      message: `Navigating to ${orientation.location}...`,
      sources: this.findRelevantSources(query, orientation),
      currentLocation: orientation.location,
    };
    onUpdate?.(navigating);
    await this.delay(800);

    // Orienting phase - extract from navigation result
    const anchors = navigationResult?.location?.landmarks || [];
    const routes = navigationResult?.orientation?.nearbyTopics || [];

    const orienting: AtlasSearchProcess = {
      ...navigating,
      status: 'orienting',
      message: `Orienting in ${orientation.location}. Found ${anchors.length} landmarks and ${routes.length} routes. This information lives here.`,
      currentLocation: orientation.location,
      nearbyAreas: routes.map((r: any) => r.name || r.label || "").filter(Boolean),
    };
    onUpdate?.(orienting);
    await this.delay(500);

    // Complete
    const complete: AtlasSearchProcess = {
      ...orienting,
      status: 'complete',
      message: `Found relevant landmarks and topics in ${orientation.location}. Ready to provide information.`,
    };
    onUpdate?.(complete);

    return complete;
  }

  /**
   * Generate navigation queries (for UI display)
   */
  private generateNavigationQueries(query: string): string[] {
    // Generate contextual queries based on the actual query
    const queries: string[] = [];
    
    // Extract key terms
    const terms = query.toLowerCase().split(/\s+/).filter(term => term.length > 2);
    
    if (terms.length > 0) {
      queries.push(`${query} documentation`);
      queries.push(`${query} concepts`);
      queries.push(`${query} related topics`);
    } else {
      queries.push(`${query} information`);
    }
    
    return queries.slice(0, 4);
  }

  /**
   * Find relevant sources (landmarks and topics) - now uses real data
   */
  private findRelevantSources(query: string, orientation: Orientation): Array<{ name: string; url: string; type: 'landmark' | 'topic' }> {
    // Return sources based on actual orientation
    const sources: Array<{ name: string; url: string; type: 'landmark' | 'topic' }> = [
      {
        name: orientation.location,
        url: `#${orientation.location.toLowerCase().replace(/\s+/g, "-")}`,
        type: 'topic',
      },
      {
        name: `${orientation.area} Area`,
        url: `#${orientation.area.toLowerCase().replace(/\s+/g, "-")}`,
        type: 'topic',
      },
    ];

    // Add query-specific sources
    if (query.toLowerCase().includes("api")) {
      sources.push({ name: "API Documentation", url: "#api-docs", type: "landmark" });
    }
    if (query.toLowerCase().includes("code") || query.toLowerCase().includes("programming")) {
      sources.push({ name: "Code Examples", url: "#code-examples", type: "landmark" });
    }

    return sources;
  }

  /**
   * Get current location in cognitive map - now uses real orientation
   */
  private getCurrentLocation(query: string): string {
    // This will be updated by getSearchProcess with real orientation
    return "Determining location...";
  }

  /**
   * Get nearby areas - now uses real routes
   */
  private getNearbyAreas(query: string): string[] {
    // This will be updated by getSearchProcess with real routes
    return ["Exploring nearby areas..."];
  }

  /**
   * Delay utility
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get navigation history
   */
  getHistory(): AtlasNavigationResult[] {
    return [...this.navigationHistory];
  }

  /**
   * Clear navigation history
   */
  clearHistory(): void {
    this.navigationHistory = [];
  }
}

// Singleton instance
let atlasInstance: AtlasIntegration | null = null;

export function getAtlas(): AtlasIntegration {
  if (!atlasInstance) {
    atlasInstance = new AtlasIntegration();
  }
  return atlasInstance;
}
